import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';

import { ToastService } from '@libs/src/toast/toast.service';

import { DialogConfirmPromptComponent } from '@libs/src/dialog-confirm-prompt/dialog-confirm-prompt.component';

import { Account } from '@libs/src/models/account.model';
import { TOAST_MESSAGE_REPEAT_PUBLISHED } from '@main-client/src/app/events/event-form/event-form.helpers';
import {
  confirmOpportunitySubmissions,
  updateOpportunitySubmissionForUser,
} from '@main-client/src/app/opportunity/opportunity.helper';
import {
  Opportunity,
  OpportunitySubmission,
} from '@main-client/src/app/opportunity/opportunity.model';

import castArray from 'lodash-es/castArray';
import cloneDeep from 'lodash-es/cloneDeep';
import get from 'lodash-es/get';
import head from 'lodash-es/head';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import omit from 'lodash-es/omit';
import set from 'lodash-es/set';

const OPPORTUNITY_API = 'api/opportunity';
const OPPORTUNITY_SUBMISSION_API = 'api/opportunity-submission';
const OPPORTUNITIES_API = 'api/v2/opportunities';
const MAX_RSVP_ERROR_NAME = 'MaxRsvpError';
const OPPORTUNITY_FIELDS_TO_SANITIZE = [
  {
    key: 'featured_accounts',
    defaultValue: [],
  },
  {
    key: 'owner_group',
    defaultValue: '',
  },
  {
    key: 'restricted_group',
    defaultValue: '',
  },
];
const OMIT_OPPORTUNITY_KEYS_FOR_PATCH = [
  'featured_accounts',
  'owner_group',
  'restricted_group',
];

@Injectable()
export class OpportunityService {
  constructor(
    private dialog: MatDialog,
    private http: HttpClient,
    private toastService: ToastService,
  ) {}

  updateUserSubmissionForOpportunity(opportunity: Opportunity, user: Account) {
    return this.updateOpportunitySubmission(opportunity).pipe(
      map((submission: OpportunitySubmission) =>
        updateOpportunitySubmissionForUser(opportunity, submission, user),
      ),
      catchError((response: any) => {
        const error = get(response, 'error.message', response.message);
        if (isEqual(MAX_RSVP_ERROR_NAME, response.error.name)) {
          const dialogConfig: any = {
            data: {
              ...this.maxUserSubmissionsDialog(error),
            },
          };
          this.dialog.open(DialogConfirmPromptComponent, dialogConfig);
        } else {
          this.toastService.showError(error);
        }
        return throwError(error);
      }),
    );
  }

  confirmSubmissionsForOpportunity(
    opportunity: Opportunity,
    confirmedSubmissions: OpportunitySubmission[],
  ) {
    const confirmSubmissionsParams = {
      eventId: opportunity._id,
      submissions: { selectedRsvps: confirmedSubmissions },
    };
    return this.confirmOpportunitySubmissions(confirmSubmissionsParams).pipe(
      map(() =>
        confirmOpportunitySubmissions(opportunity, confirmedSubmissions),
      ),
      catchError((response: any) => {
        const error = get(response, 'error.message', response.message);
        this.toastService.showError(error);
        return throwError(error);
      }),
    );
  }

  postOpportunity(opportunity: Opportunity) {
    const opportunityToPost = this.mapValueToDocumentKeys(
      opportunity,
      OPPORTUNITY_FIELDS_TO_SANITIZE,
      'slug',
    );
    return this.createOpportunity(opportunityToPost).pipe(
      map((updatedOpportunity: Opportunity) => {
        return {
          ...opportunity,
          ...omit(
            updatedOpportunity,
            OPPORTUNITY_FIELDS_TO_SANITIZE.map((field: any) => field.key),
          ),
        };
      }),
      catchError((response: any) => {
        const error = get(response, 'error.message', response.message);
        this.toastService.showError(error);
        return throwError(error);
      }),
    );
  }

  patchOpportunity({
    initialOpportunity,
    update,
    putFeaturedAccount,
    deleteFeaturedAccount,
  }: any) {
    const putFeaturedAccountObservable = !isEmpty(putFeaturedAccount)
      ? this.putFeaturedAccount(initialOpportunity._id, putFeaturedAccount._id)
      : of({});
    const deleteFeaturedAccountObservable = !isEmpty(deleteFeaturedAccount)
      ? this.deleteFeaturedAccount(
          initialOpportunity._id,
          deleteFeaturedAccount._id,
        )
      : of({});
    return combineLatest([
      putFeaturedAccountObservable,
      deleteFeaturedAccountObservable,
    ]).pipe(
      concatMap(() => this.updateOpportunity(update)),
      map((updatedOpportunity: Opportunity) => {
        return {
          ...update,
          ...omit(
            updatedOpportunity,
            OPPORTUNITY_FIELDS_TO_SANITIZE.map((field: any) => field.key),
          ),
        };
      }),
      catchError((response: any) => {
        const error = get(response, 'error.message', response.message);
        this.toastService.showError(error);
        return throwError(error);
      }),
    );
  }

  updateOpportunitySubmission(opportunity: Opportunity) {
    const update = {
      opportunityId: opportunity._id,
      submissions: get(opportunity, 'user_submission.submissions', []),
    };
    return this.http
      .put(OPPORTUNITY_SUBMISSION_API, update)
      .pipe(map((response: { data: OpportunitySubmission }) => response.data));
  }

  getOpportunityForTenant(opportunity: Opportunity) {
    return this.http.get(`${OPPORTUNITY_API}/${opportunity.slug}`).pipe(
      map((response: { data: Opportunity }) => response.data),
      catchError((response: any) => {
        const error = get(response, 'error.message', response.message);
        this.toastService.showError(error);
        return of({ error: response.error });
      }),
    );
  }

  confirmOpportunitySubmissions(params: any) {
    return this.http.put(
      `${OPPORTUNITIES_API}/${params.eventId}/submissions/select`,
      params.submissions,
    );
  }

  createOpportunity(opportunity: Opportunity): Observable<any> {
    return this.http
      .post(OPPORTUNITY_API, opportunity)
      .pipe(map((response: any) => response.data));
  }

  mapValueToDocumentKeys(
    opportunity: Opportunity,
    opportunityFieldsToSanitize: { key: string; defaultValue: any }[],
    documentKey: string,
  ) {
    return opportunityFieldsToSanitize.reduce(
      (
        opportunityClone: Opportunity,
        opportunityField: { key: string; defaultValue: any },
      ) => {
        const valueOfOpportunityField = get(
          opportunityClone,
          `${opportunityField.key}`,
          opportunityField.defaultValue,
        );
        if (Array.isArray(valueOfOpportunityField)) {
          const valueToSet = castArray(
            get(
              head(valueOfOpportunityField),
              documentKey,
              opportunityField.defaultValue,
            ),
          );
          set(opportunityClone, opportunityField.key, valueToSet);
        } else {
          const valueToSet = get(
            valueOfOpportunityField,
            documentKey,
            opportunityField.defaultValue,
          );
          set(opportunityClone, opportunityField.key, valueToSet);
        }
        return opportunityClone;
      },
      cloneDeep({ ...opportunity }),
    );
  }

  updateOpportunity(opportunity: Opportunity): Observable<any> {
    return this.http.patch(
      `${OPPORTUNITIES_API}/${opportunity._id}`,
      omit(opportunity, OMIT_OPPORTUNITY_KEYS_FOR_PATCH),
    );
  }

  deleteFeaturedAccount(
    opportunityId: string,
    accountId: string,
  ): Observable<any> {
    return this.http.delete(
      `${OPPORTUNITIES_API}/${opportunityId}/featured_accounts/${accountId}`,
    );
  }

  putFeaturedAccount(
    opportunityId: string,
    accountId: string,
  ): Observable<any> {
    return this.http.put(
      `${OPPORTUNITIES_API}/${opportunityId}/featured_accounts/${accountId}`,
      {},
    );
  }

  postRepeatedOpportunities(opportunities: Opportunity[]) {
    const postOpportunityObservables = opportunities.map(
      (opportunity: Opportunity) => {
        const opportunityToPost = this.mapValueToDocumentKeys(
          opportunity,
          OPPORTUNITY_FIELDS_TO_SANITIZE,
          'slug',
        );
        return this.createOpportunity(opportunityToPost);
      },
    );
    return combineLatest(postOpportunityObservables).pipe(
      map(() => this.toastService.showSuccess(TOAST_MESSAGE_REPEAT_PUBLISHED)),
      catchError((response: any) => {
        const error = get(response, 'error.message', response.message);
        this.toastService.showError(error);
        return throwError(error);
      }),
    );
  }

  maxUserSubmissionsDialog(error: string): any {
    return {
      title: 'Maximum registrations reached',
      cancel: 'Got It',
      content: $localize`:@@confirmPromptDialog.content.maxRsvp:${error} If you'd like to join this event, please view your upcoming Office Hours and remove your registration from a different event.`,
    };
  }
}
